import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import {
  Cancel,
  CheckCircleOutline,
  SettingsBackupRestore,
} from "@material-ui/icons";
import { IconButton, Input, Tooltip } from "@material-ui/core";
import { sanitizeTextWithSeparators } from "./utils";

const shaking = keyframes`
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
`;

const Box = styled.div`
  flex: 1;
  padding: 8px;
  margin: 16px 8px 0 8px;
  border: 1px solid #028DFF;
  color: #2948FF;
  background-Color: #E0EEFD;
  border-radius: 12px;
  position: relative;
  text-align: center;
  font-weight: ${(p: any) => (p.shake ? "bold" : "normal")}  
  max-width: 80px;
  min-width: 80px;
  animation: ${(p: any) =>
    p.shake
      ? css`
          ${shaking} 200ms infinite
        `
      : "none"} ;

`;

const styles = (theme: Theme) =>
  createStyles({
    box: {
      padding: `${theme.spacing.unit}px`,
      border: `1px solid #028DFF`,
      color: "#2948FF",
      backgroundColor: "#E0EEFD",
      borderRadius: `${theme.shape.borderRadius}px`,
      position: "relative",
      textAlign: "center",
      width: 75,
    },
    label: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    button: {
      display: "box",
      position: "absolute",
      border: "none",
      right: -9,
      top: -9,
      borderRadius: "50px",
      backgroundColor: "#FFF",
      color: "#FFF",
      lineHeight: "15px",
      "&.sortable-ghost": {
        backgroundColor: "#c8ebfb",
      },
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#FFF",
      },
    },
    wobble: {
      animation: `$wobbling`,
    },
    validateButton: {
      display: "box",
      border: "none",
      borderRadius: "50px",
      backgroundColor: "#028DFF",
      color: "#FFF",
      lineHeight: "15px",
      "&.sortable-ghost": {
        backgroundColor: "#c8ebfb",
      },
      "&:hover": {
        cursor: "pointer",
      },
    },
  });

const Tag = (props: any) => {
  const {
    classes,
    label,
    withDelete,
    onDelete,
    onValidateChange,
    item,
    editable,
    ...rest
  } = props;
  const [inputValue, setInputValue] = useState(label ?? "");
  const [modify, setModify] = useState(false);
  const [modified, setModified] = useState(false);

  const handleDelete = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const val = onDelete();
    setInputValue(val);
    setModified(false);
  };

  const handleChange = (e: any) => {
    setInputValue(sanitizeTextWithSeparators(e.target.value));
  };

  const validateChange = () => {
    const normalizedValue = (inputValue || "").trim();
    if (normalizedValue !== "") {
      onValidateChange(normalizedValue);
      setInputValue(normalizedValue);
      setModify(false);
      setModified(true);
    }
  };

  return (
    <Box {...rest} className={classes.box}>
      {editable && modify ? (
        <div title={label}>
          <Input
            value={inputValue}
            autoFocus={true}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                validateChange();
              }
            }}
            endAdornment={
              <IconButton style={{ padding: 0 }} onClick={validateChange}>
                <CheckCircleOutline fontSize="small" color="primary" />
              </IconButton>
            }
          />
        </div>
      ) : (
        <Tooltip title={label}>
          <div
            className={classes.label}
            style={{ cursor: editable ? "pointer" : "default" }}
            onClick={() => {
              setModify(true);
            }}
          >
            {label}
          </div>
        </Tooltip>
      )}
      {withDelete && (
        <IconButton
          className={classes.button}
          style={{ padding: 0 }}
          onClick={handleDelete}
        >
          {modified && item.original ? (
            <SettingsBackupRestore fontSize="small" color="primary" />
          ) : (
            <Cancel fontSize="small" color="primary" />
          )}
        </IconButton>
      )}
    </Box>
  );
};

Tag.defaultProps = {
  noSuppr: false,
  withDelete: false,
};

export default withStyles(styles)(Tag);
